import React from 'react';

import { useCheckoutLoadingState, useServiceModeWidget } from 'features/checkout/hooks';
import { StyleSheet, View } from 'react-native';
import { Typography } from 'ucl/components/text/Typography';

import PickupTimeSelector from 'components/pickup-time-selector';
import { PickupNowEtaIndicator } from 'components/pickup-time-selector/pickup-now-eta-indicator';
import { ActionButton } from 'components/ui/action-button';
import { useIsLargeFontScale } from 'hooks/use-is-large-font-scale';
import { PickupTimeHeadingContainer } from 'pages/cart/details/styled';
import SectionHeading from 'pages/cart/section-heading';
import { AmplitudeCheckoutSection, getCheckoutChangeButtonEventAttributes } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useServiceModeLive } from 'state/service-mode';
import { ServiceMode } from 'state/service-mode/types';
import { primitive, spacing } from 'styles/constants';

import { WidgetBaseStyles } from '../../pages/styled';

import { ServiceModeSelector } from './components/service-mode-selector';
import { VehicleDescriptionMobilePickup } from './components/vehicle-description-mobile-pickup';
import { IconStyled } from './styled';

export const ServiceModeWidget: React.FC<React.PropsWithChildren> = () => {
  const {
    selectedServiceMode,
    selectedServiceModeAccessibilityLabel,
    serviceModeOptions,
    handleOpenServiceModeSelector,
    handleSelectServiceMode,
    isServiceModeSelectorOpen,
    setIsServiceModeSelectorOpen,
    setVehicleDescription,
    vehicleDescriptionValue,
    vehicleDescriptionHasError,
    displayVehicleDescription,
    isOrderTimedFire,
    isDisabled,
  } = useServiceModeWidget();
  const serviceMode = useServiceModeLive();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const { isCheckoutLoading } = useCheckoutLoadingState();
  const isLargeFontScale = useIsLargeFontScale();

  if (!selectedServiceMode?.isAvailable) {
    return null;
  }

  return (
    <>
      <View>
        <View
          testID="service-mode-widget"
          style={[
            styles.container,
            isDisabled && styles.disabledContainer,
            isLargeFontScale && styles.stackedContainer,
          ]}
        >
          <View style={[styles.serviceModeRow, isLargeFontScale && styles.stackedServiceModeRow]}>
            <IconStyled
              variant={selectedServiceMode?.icon}
              style={styles.icon}
              disabled={isDisabled}
            />
            <View style={styles.serviceModeDetails}>
              <Typography
                accessible
                accessibilityRole="text"
                accessibilityLabel={selectedServiceModeAccessibilityLabel}
                style={[styles.serviceModeNameText, isDisabled && styles.disabledText]}
              >
                {selectedServiceMode?.heading}
              </Typography>
              <View style={styles.space4} />
              <Typography
                style={[styles.serviceModeDescriptionText, isDisabled && styles.disabledText]}
                numberOfLines={2}
              >
                {selectedServiceMode?.description}
              </Typography>
            </View>
          </View>
          <ActionButton
            onPress={handleOpenServiceModeSelector}
            testID="change-service-mode-button"
            accessibilityLabel={t('changeServiceMode')}
            disabled={isDisabled || isCheckoutLoading}
            eventAttributes={getCheckoutChangeButtonEventAttributes(
              AmplitudeCheckoutSection.ServiceMode
            )}
            width="auto"
            fullWidth={isLargeFontScale}
            customStyles={isLargeFontScale ? styles.stackedChangeButton : styles.changeButton}
          >
            {t('change')}
          </ActionButton>
        </View>
        <View style={styles.footerContainer}>
          <Typography
            testID="service-modes-text"
            style={[styles.serviceModeOptionsText, isDisabled && styles.disabledText]}
          >
            {serviceModeOptions.map(serviceMode => serviceMode.heading).join(', ')}
          </Typography>
        </View>
        {displayVehicleDescription && (
          <VehicleDescriptionMobilePickup
            onChange={setVehicleDescription}
            value={vehicleDescriptionValue}
            hasError={vehicleDescriptionHasError}
          />
        )}
        {isOrderTimedFire && (
          <View>
            <PickupTimeHeadingContainer>
              <SectionHeading>{isDelivery ? t('deliveryTime') : t('pickUpTime')}</SectionHeading>
              <PickupNowEtaIndicator />
            </PickupTimeHeadingContainer>
            <PickupTimeSelector isDisabled={isCheckoutLoading} />
          </View>
        )}
      </View>
      {serviceModeOptions && (
        <ServiceModeSelector
          isOpen={isServiceModeSelectorOpen}
          onClose={() => setIsServiceModeSelectorOpen(false)}
          serviceModeOptions={serviceModeOptions}
          handleSelectServiceMode={handleSelectServiceMode}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    ...WidgetBaseStyles.container,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  stackedContainer: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: spacing.$2,
  },
  serviceModeDetails: {
    flexDirection: 'column',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  serviceModeRow: {
    flexDirection: 'row',
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    alignItems: 'flex-start',
  },
  stackedServiceModeRow: {
    flexGrow: 0,
    width: '100%',
  },
  changeButton: {
    flexShrink: 0,
  },
  stackedChangeButton: {
    flexShrink: 0,
    alignSelf: 'stretch',
    width: '100%',
  },
  serviceModeNameText: {
    fontSize: 16,
    lineHeight: 16,
    fontFamily: primitive.fontFamily.$headers,
    color: primitive.$bbqBrown,
  },
  serviceModeDescriptionText: {
    lineHeight: 16,
    fontSize: 14,
    marginRight: 4,
    fontFamily: primitive.fontFamily.$body,
    color: primitive.$bbqBrown,
  },
  icon: {
    marginRight: 4,
  },
  footerContainer: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    backgroundColor: primitive.$blackOpacity4,
  },
  serviceModeOptionsText: {
    color: primitive.$blackOpacity70,
    fontSize: 10,
    fontFamily: primitive.fontFamily.$body,
    textAlign: 'right',
  },
  space4: {
    marginTop: 4,
  },
  disabledContainer: {
    backgroundColor: primitive.$blackOpacity4,
  },
  disabledText: {
    color: primitive.$blackOpacity50,
  },
});
